<template>
  <VContainer fluid class="pa-0 mb-8" data-testid="conclusion-and-recommandations">
    <div v-if="!nonTechnicalSummary" class="d-flex justify-end mb-4">
      <AutoSaveStatus
        :is-saving="autoSave?.saveInProgress"
        :is-dirty="autoSave?.hasUnsavedChanges"
        :last-saved="autoSave?.lastSaved"
        :save-errors="autoSave?.saveErrors"
      />
    </div>
    <VRow v-if="!nonTechnicalSummary">
      <VCol>
        <h3 class="mb-2">Mesures de sécurité :</h3>
        <p v-if="!securityMeasures.length">-</p>
        <ul v-else class="ml-6">
          <li v-for="securityMeasure in securityMeasures" :key="securityMeasure">
            {{ securityMeasure }}
          </li>
        </ul>
      </VCol>
    </VRow>
    <VRow>
      <VCol>
        <p v-if="isDiagnosticMission" class="my-4">
          <span v-if="currentMission.globalMission != 'ELEM'"
            >Conformément aux recommandations de la mission A130,</span
          >
          SOCOTEC Environnement a procédé à la réalisation d’investigations sur les milieux ({{
            invetigatedEnvironments
          }}).
        </p>
        <p v-else class="my-4">{{ safetyText }}</p>
        <h3 class="mb-2">Conclusions et recommandations supplémentaires :</h3>
        <FullWysiwyg v-if="!nonTechnicalSummary" v-model="form.conclusion" />
        <!-- eslint-disable-next-line vue/no-v-html -- sanitized with DOMPurify -->
        <p v-else class="conclusion" v-html="DOMPurify.sanitize(form.conclusion)"></p>
        <h3 class="mt-4 mb-2">{{ t("comment") }}</h3>
        <p class="mb-8">
          <strong>Nous recommandons de garder la mémoire de ce diagnostic.</strong>
          En cas de transaction impliquant tout ou partie du site, transmettre le présent rapport à
          l'acquéreur / aménageur ainsi qu’au notaire afin qu’il apparaisse dans l’acte de vente et
          que la mémoire de cette étude soit conservée.
        </p>
      </VCol>
    </VRow>

    <BaseSaveButton
      v-if="!nonTechnicalSummary"
      :button-text="t('reset')"
      button-icon="mdi-restore"
      socio-style="primary"
      @action="setConclusion"
    />
  </VContainer>
  <IncertitudeButton v-if="!nonTechnicalSummary" />
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import FullWysiwyg from "@/components/ssp/missions/mission/site/partials/FullWysiwyg.vue";
import AutoSaveStatus from "@/components/base-components/AutoSaveStatus.vue";
import BaseSaveButton from "@/components/base-components/BaseSaveButton.vue";
import IncertitudeButton from "@/components/ssp/partials/IncertitudeButton.vue";
import { useMissionStore } from "@/store/modules/mission";
import { useInvestigationSynthesisStore } from "@/store/modules/interpretation/investigationSynthesis";
import { useSiteStore } from "@/store/modules/site";
import { useStripTags } from "@/composables/missions/useStripTags";
import { useFormAutoSave } from "@/composables/useFormAutoSave";
import { useI18n } from "vue-i18n";
import DOMPurify from "dompurify";
const { t } = useI18n();
const siteStore = useSiteStore();
const missionsStore = useMissionStore();
const investigationSynthesisStore = useInvestigationSynthesisStore();

const props = defineProps({
  nonTechnicalSummary: {
    type: Boolean,
    default: false,
  },
});
const currentMission = computed(() => missionsStore.currentMission);
const securityMeasures = ref([]);
const invetigatedEnvironments = ref([]);
const sourceOption = ref("NO_SOURCE_TAG");
const autoSave = ref(null);

const form = ref({
  conclusion: "",
  comment: "",
});
const initializeAutoSave = () => {
  if (props.nonTechnicalSummary) return;

  autoSave.value = useFormAutoSave(missionsStore, form, {
    saveMethod: "updateMission",
    transformer: (data) => ({
      ...data,
      uuid: currentMission.value.uuid,
    }),
  });
  autoSave.value.initializeTracking(form.value);
};

const isDiagnosticMission = [
  "DIAG",
  "INFOS+DIAG",
  "VERIF Phase 2",
  "VERIF Phases 1 et 2",
  "ELEM",
  "IEM",
  "SUIVI",
].includes(currentMission.value.globalMission);
const safetyText = computed(() => {
  if (["INFOS", "VERIF Phase 1"].includes(currentMission.value.globalMission)) {
    return t(`SAFETY_MEASURES.${sourceOption.value}`);
  } else if (currentMission.value.globalMission === "LEVE") {
    return t(`CONCLUSION.${sourceOption.value}`);
  }
  return "";
});

onMounted(async () => {
  form.value.conclusion = currentMission.value.conclusion || "";
  form.value.comment = currentMission.value.comment || "";

  if (isDiagnosticMission) {
    invetigatedEnvironments.value = await missionsStore.getInvestigatedEnvironments(
      missionsStore.currentMissionUuid,
    );
  }
  const site = await siteStore.fetchCurrentSiteWithImmediateDangers();
  securityMeasures.value = [
    ...new Set(
      (site?.immediateDangers ?? [])
        .map((immediateDanger) => {
          return immediateDanger.securityMeasures;
        })
        .flat(),
    ),
  ];
  sourceOption.value = await missionsStore.checkSourceOption(missionsStore.currentMissionUuid);
  const conclusion = currentMission.value.conclusion;
  const conclusionTexts = [
    t("CONCLUSION.NO_SOURCE_TAG"),
    t("CONCLUSION.NO_SOURCE_RETAINED"),
    t("CONCLUSION.SOURCE_RETAINED"),
    t("CONCLUSION.DIAGNOSTIC"),
  ];
  if (!conclusion.trim() || conclusionTexts.includes(conclusion)) {
    await setConclusion();
  }

  if (!currentMission.value.comment || !useStripTags(currentMission.value.comment)) {
    await setComment();
  }

  await nextTick();
  initializeAutoSave();
});
const setConclusion = async () => {
  const isdiText =
    "Dans le cadre de la gestion des terres excavées provenant d'un site non contaminé, la réalisation d'analyses de type pack ISDI n'est pas systématiquement requise, ces matériaux pouvant être acceptés en installation de stockage de déchets inertes (ISDI) sans analyse préalable, sous réserve qu'ils figurent dans la liste des déchets admissibles sans caractérisation, conformément à l'annexe 1 de l'arrêté ministériel du 12 décembre 2014. Les filières de réception des déblais sont néanmoins susceptibles d'exiger ces analyses préalables, qu'il conviendra alors de réaliser en prévision des travaux de terrassement.";

  if (currentMission.value.globalMission === "LEVE") {
    form.value.conclusion = isdiText;
  } else if (["INFOS", "VERIF Phase 1"].includes(currentMission.value.globalMission)) {
    if (sourceOption.value === "NO_SOURCE_TAG") {
      form.value.conclusion = isdiText;
    } else {
      form.value.conclusion =
        "En cas d’excavation et de la gestion des déblais, des analyses de type ISDI devront être réalisées afin de déterminer l’orientation des terres. ";
    }
  } else if (isDiagnosticMission) {
    const investigationSynthesis = await investigationSynthesisStore.fetchInvestigationSynthesis();
    form.value.conclusion =
      (investigationSynthesis?.synthesisText ?? "") + t("CONCLUSION.DIAGNOSTIC");
  }

  await missionsStore.updateMission({
    ...currentMission.value,
    conclusion: form.value.conclusion,
  });

  if (autoSave.value) {
    autoSave.value.initializeTracking(form.value);
  }
};
const setComment = async () => {
  if (sourceOption.value === "SOURCE_RETAINED") {
    form.value.comment =
      "Dans le cadre de la gestion des terres excavées provenant d'un site non contaminé, la réalisation d'analyses de type pack ISDI n'est pas systématiquement requise, ces matériaux pouvant être acceptés en installation de stockage de déchets inertes (ISDI) sans analyse préalable, sous réserve qu'ils figurent dans la liste des déchets admissibles sans caractérisation, conformément à l'annexe 1 de l'arrêté ministériel du 12 décembre 2014. Les filières de réception des déblais sont néanmoins susceptibles d'exiger ces analyses préalables, qu'il conviendra alors de réaliser en prévision des travaux de terrassement.";
  } else {
    form.value.comment =
      "En cas d'excavation et de la gestion des déblais, des analyses de type ISDI devront être réalisées afin de déterminer l'orientation des terres.";
  }

  await missionsStore.updateMission({
    ...currentMission.value,
    comment: form.value.comment,
  });

  if (autoSave.value) {
    autoSave.value.initializeTracking(form.value);
  }
};
</script>

<style lang="scss" scoped>
h3 {
  letter-spacing: 0;
  font-weight: 700;
  line-height: 1.125rem;
  font-size: 1rem;
}

p {
  font-weight: 400;
  letter-spacing: 0;
  line-height: 100%;
  font-size: 0.875rem;
}
.conclusion :deep(ul) {
  margin-left: 30px !important;
}
.comment :deep(ul) {
  margin-left: 30px !important;
}
</style>

<i18n>
  {
  "en": {
    "reset": "Reset",
    "comment": "Comment"
  },
  "fr": {
    "reset": "Reinitialiser",
    "comment": "Commentaire"
  }
  }
</i18n>
